import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { articlesApi, workspacesApi, pillarsApi } from '../lib/api';
import { Spinner, Alert, StatCard, EmptyState, StatusBadge } from '@rankhelm/ui';
import type { Article, ArticleStatus, Workspace, Pillar } from '@rankhelm/shared';

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  generating: 'Generating...',
  draft: 'Draft',
  in_review: 'In Review',
  revision_requested: 'Revision Requested',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing...',
  published: 'Published',
  publish_failed: 'Publish Failed',
};

type SortKey = 'title' | 'status' | 'geo_score' | 'word_count' | 'created_at';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 20;

export default function ArticleQueue() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [pillarFilter, setPillarFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [articlesRes, pillarsRes, wsRes] = await Promise.all([
        articlesApi.list(workspaceId, {
          status: (statusFilter || undefined) as ArticleStatus | undefined,
          pillar_id: pillarFilter || undefined,
        }),
        pillarsApi.list(workspaceId),
        workspacesApi.get(workspaceId),
      ]);
      setArticles(articlesRes.data || []);
      setPillars(pillarsRes.data || []);
      setWorkspace(wsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, statusFilter, pillarFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Poll for generating articles
  useEffect(() => {
    const hasGenerating = articles.some(a => a.status === 'generating');
    if (!hasGenerating) return;
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [articles, loadData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, pillarFilter, searchQuery, sortKey, sortDir]);

  // Filtered, searched, sorted articles
  const processedArticles = useMemo(() => {
    let result = [...articles];

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        (a.target_keyword && a.target_keyword.toLowerCase().includes(q))
      );
    }

    // Sort
    result.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'title':
          return dir * a.title.localeCompare(b.title);
        case 'status':
          return dir * a.status.localeCompare(b.status);
        case 'geo_score':
          return dir * ((a.geo_score ?? 0) - (b.geo_score ?? 0));
        case 'word_count':
          return dir * ((a.word_count ?? 0) - (b.word_count ?? 0));
        case 'created_at':
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        default:
          return 0;
      }
    });

    return result;
  }, [articles, searchQuery, sortKey, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(processedArticles.length / PAGE_SIZE));
  const paginatedArticles = processedArticles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleGenerate = async (articleId: string) => {
    setGeneratingIds(prev => new Set(prev).add(articleId));
    setError('');
    try {
      await articlesApi.generate(articleId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate article');
    } finally {
      setGeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedIds.size === 0) return;
    setBulkGenerating(true);
    setError('');
    try {
      await articlesApi.bulkGenerate(workspaceId!, Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk generate');
    } finally {
      setBulkGenerating(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllQueued = () => {
    const queued = articles.filter(a => a.status === 'queued').map(a => a.id);
    setSelectedIds(new Set(queued));
  };

  const unselectAll = () => {
    setSelectedIds(new Set());
  };

  const pillarName = (id: string | null) => {
    if (!id) return 'Uncategorized';
    const pl = pillars.find(p => p.id === id);
    return pl?.name || 'Unknown';
  };

  // Stats
  const stats = {
    queued: articles.filter(a => a.status === 'queued').length,
    generating: articles.filter(a => a.status === 'generating').length,
    draft: articles.filter(a => a.status === 'draft').length,
    in_review: articles.filter(a => a.status === 'in_review').length,
    approved: articles.filter(a => a.status === 'approved').length,
    published: articles.filter(a => a.status === 'published').length,
    total: articles.length,
  };

  if (loading) {
    return <Spinner message="Loading article queue..." fullPage />;
  }

  return (
    <div className="px-8 py-6 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Article Queue</h1>
          <p className="text-sm text-slate-400 mt-1">{workspace?.name}</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkGenerate}
              disabled={bulkGenerating}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-semibold text-sm rounded-lg transition-colors disabled:bg-gray-700 disabled:cursor-default"
            >
              {bulkGenerating ? 'Generating...' : `Generate ${selectedIds.size} Selected`}
            </button>
          )}
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {/* Stats Bar */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Queued" value={stats.queued} />
        <StatCard label="Generating" value={stats.generating} />
        <StatCard label="Draft" value={stats.draft} />
        <StatCard label="In Review" value={stats.in_review} />
        <StatCard label="Approved" value={stats.approved} />
        <StatCard label="Published" value={stats.published} />
      </div>

      {/* Search + Filters + Sort */}
      <div className="flex gap-3 mb-5 items-center flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[360px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search by title or keyword..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-navy-900 text-slate-200 border border-navy-600 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-helm-500 placeholder:text-slate-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-navy-900 text-slate-200 border border-navy-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-helm-500"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select
          value={pillarFilter}
          onChange={e => setPillarFilter(e.target.value)}
          className="bg-navy-900 text-slate-200 border border-navy-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-helm-500"
        >
          <option value="">All Pillars</option>
          {pillars.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        {/* Sort dropdown */}
        <select
          value={`${sortKey}:${sortDir}`}
          onChange={e => {
            const [key, dir] = e.target.value.split(':');
            setSortKey(key as SortKey);
            setSortDir(dir as SortDir);
          }}
          className="bg-navy-900 text-slate-200 border border-navy-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-helm-500"
        >
          <option value="created_at:desc">Newest First</option>
          <option value="created_at:asc">Oldest First</option>
          <option value="title:asc">Title A-Z</option>
          <option value="title:desc">Title Z-A</option>
          <option value="geo_score:desc">GEO Score High-Low</option>
          <option value="geo_score:asc">GEO Score Low-High</option>
          <option value="word_count:desc">Word Count High-Low</option>
          <option value="status:asc">Status A-Z</option>
        </select>

        {stats.queued > 0 && (
          <button
            onClick={selectAllQueued}
            className="px-4 py-2 bg-navy-900 text-slate-400 border border-navy-600 rounded-lg cursor-pointer text-sm hover:text-white transition-colors"
          >
            Select All Queued ({stats.queued})
          </button>
        )}
        {selectedIds.size > 0 && (
          <button
            onClick={unselectAll}
            className="px-4 py-2 bg-navy-900 text-slate-400 border border-navy-600 rounded-lg cursor-pointer text-sm hover:text-white transition-colors"
          >
            Unselect All
          </button>
        )}
      </div>

      {/* Results count */}
      {searchQuery.trim() && (
        <p className="text-xs text-slate-500 mb-3">
          {processedArticles.length} result{processedArticles.length !== 1 ? 's' : ''} found
        </p>
      )}

      {/* Article List */}
      <div className="flex flex-col gap-2">
        {paginatedArticles.length === 0 ? (
          <EmptyState
            message={
              searchQuery.trim()
                ? 'No articles match your search. Try a different search term.'
                : 'No articles found. Generate content pillars first, then articles will appear here.'
            }
          />
        ) : (
          paginatedArticles.map(article => {
            const isSelected = selectedIds.has(article.id);
            const isSelectable = article.status === 'queued';

            return (
              <div
                key={article.id}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('a') || target.closest('button')) return;
                  if (isSelectable) toggleSelect(article.id);
                }}
                className={`bg-slate-800 rounded-[10px] px-5 py-4 flex items-center gap-4 transition-colors border-2 ${
                  isSelected
                    ? 'border-purple-500'
                    : isSelectable
                      ? 'border-slate-700 hover:border-purple-500/50'
                      : 'border-slate-700'
                } ${isSelectable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {/* Status badge */}
                <StatusBadge status={article.status} className="w-[130px]" />

                {/* Article info */}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/workspaces/${workspaceId}/articles/${article.id}`}
                    className="text-slate-100 hover:text-helm-400 text-[15px] font-medium truncate block transition-colors"
                  >
                    {article.title}
                  </Link>
                  <div className="flex gap-4 mt-1 text-xs text-slate-500">
                    {(article.geo_score ?? 0) > 0 && (
                      <span className={
                        (article.geo_score ?? 0) >= 80 ? 'text-green-400' :
                        (article.geo_score ?? 0) >= 60 ? 'text-yellow-300' : 'text-red-400'
                      }>
                        GEO: {article.geo_score}/100
                      </span>
                    )}
                    {article.word_count > 0 && <span>{article.word_count.toLocaleString()} words</span>}
                    <span>{pillarName(article.pillar_id)}</span>
                    {article.target_keyword && <span className="text-slate-400">{article.target_keyword}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 items-center shrink-0">
                  {article.status === 'queued' && (
                    <button
                      onClick={() => handleGenerate(article.id)}
                      disabled={generatingIds.has(article.id)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors disabled:bg-slate-600 disabled:cursor-default whitespace-nowrap"
                    >
                      {generatingIds.has(article.id) ? 'Generating...' : 'Generate'}
                    </button>
                  )}
                  {['draft', 'in_review', 'approved', 'revision_requested'].includes(article.status) && (
                    <button
                      onClick={() => navigate(`/workspaces/${workspaceId}/articles/${article.id}`)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                    >
                      {article.status === 'revision_requested' ? 'Review Revision' : 'Review'}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-xs text-slate-500">
            Showing {((page - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(page * PAGE_SIZE, processedArticles.length)} of {processedArticles.length} articles
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 bg-slate-800 text-slate-300 border border-slate-700 rounded-lg text-sm transition-colors hover:bg-slate-700 disabled:opacity-40 disabled:cursor-default"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                  pageNum === page
                    ? 'bg-helm-500 border-helm-500 text-white'
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {pageNum}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 bg-slate-800 text-slate-300 border border-slate-700 rounded-lg text-sm transition-colors hover:bg-slate-700 disabled:opacity-40 disabled:cursor-default"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
